import { ApiError } from '@central/api';
import { color } from '@central/ui';
import { Alert, Button, PasswordInput, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { errorMessage } from '../format';
import { AuthLayout, FormFrame } from './SignIn';

/** The server's password policy: 12 to 256 characters. */
const MIN_PASSWORD = 12;
const MAX_PASSWORD = 256;

const body = { margin: 0, fontSize: 14, lineHeight: '21px', color: color.textBody } as const;

/**
 * Forgot password (`/forgot-password`): asks for the account's email and
 * sends a reset link. The answer is the same whether or not the email has an
 * account, so the page never says which.
 */
export function ForgotPassword() {
  const search = useSearch({ from: '/forgot-password' });
  const [email, setEmail] = useState(search.email ?? '');
  const [missing, setMissing] = useState(false);
  const send = useMutation({ mutationFn: () => api.auth.forgotPassword({ email: email.trim() }) });

  if (send.isSuccess) {
    return (
      <AuthLayout>
        <FormFrame title="Check your email">
          <p style={body}>
            If {email.trim()} has a GoATMS Central account with a password, a link to choose a new
            one is on its way. It works once, for 30 minutes.
          </p>
          <p style={body}>
            No email after a few minutes? Ask an Owner in your organization to check your account,
            or your Rethink account manager.
          </p>
          <Link to="/sign-in" style={{ color: color.link, fontSize: 14 }}>
            Back to sign in
          </Link>
        </FormFrame>
      </AuthLayout>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const empty = !email.trim();
    setMissing(empty);
    if (!empty) send.mutate();
  };
  return (
    <AuthLayout>
      <FormFrame title="Reset your password">
        <p style={body}>We’ll email you a link to choose a new password.</p>
        <form
          onSubmit={submit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <TextInput
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            error={missing ? 'Enter your work email.' : undefined}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          {send.error && <Alert color="red">{errorMessage(send.error)}</Alert>}
          <Button type="submit" fullWidth loading={send.isPending}>
            Send reset link
          </Button>
        </form>
        <Link to="/sign-in" style={{ color: color.link, fontSize: 14 }}>
          Back to sign in
        </Link>
      </FormFrame>
    </AuthLayout>
  );
}

/**
 * Reset password (`/reset-password#<token>`), from the emailed link. The
 * token travels in the URL fragment, which browsers never send to a server;
 * it is read once and dropped from the address bar.
 */
export function ResetPassword() {
  const hash = useRouterState({ select: (s) => s.location.hash });
  const navigate = useNavigate();
  const [token] = useState(() => hash.replace(/^#/, ''));
  useEffect(() => {
    if (hash) void navigate({ to: '/reset-password', hash: '', replace: true });
  }, [hash, navigate]);
  const [done, setDone] = useState(false);

  return (
    <AuthLayout>
      {!token ? (
        <InvalidLink />
      ) : done ? (
        <FormFrame title="Password changed">
          <p style={body}>
            Every browser that was signed in to your account is signed out. Sign in with the new
            password; you’ll be asked for your second factor too.
          </p>
          <Button component={Link} to="/sign-in" fullWidth>
            Sign in
          </Button>
        </FormFrame>
      ) : (
        <ResetForm token={token} onDone={() => setDone(true)} />
      )}
    </AuthLayout>
  );
}

function InvalidLink() {
  return (
    <FormFrame title="This reset link is not valid">
      <p style={body}>
        It may have expired (links work for 30 minutes), been used already, or been replaced by a
        newer one. Ask for a new link.
      </p>
      <Button component={Link} to="/forgot-password" fullWidth>
        Send a new link
      </Button>
    </FormFrame>
  );
}

function ResetForm({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const reset = useMutation({
    mutationFn: () => api.auth.resetPassword({ token, password }),
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError && e.fields?.password) setFieldError(`Use ${e.fields.password}.`);
    },
  });
  if (reset.error instanceof ApiError && reset.error.code === 'invalid_token')
    return <InvalidLink />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const n = [...password].length;
    const problem =
      n < MIN_PASSWORD
        ? `Use at least ${MIN_PASSWORD} characters.`
        : n > MAX_PASSWORD
          ? `Use at most ${MAX_PASSWORD} characters.`
          : password !== repeat
            ? 'The two passwords do not match.'
            : undefined;
    setFieldError(problem);
    if (!problem) reset.mutate();
  };
  const err = reset.error;
  const fieldShown = err instanceof ApiError && err.fields?.password;
  return (
    <FormFrame title="Choose a new password">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <PasswordInput
          label="New password"
          description={`At least ${MIN_PASSWORD} characters.`}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <PasswordInput
          label="Repeat the new password"
          autoComplete="new-password"
          value={repeat}
          error={fieldError}
          onChange={(e) => setRepeat(e.currentTarget.value)}
        />
        {err && !fieldShown && <Alert color="red">{errorMessage(err)}</Alert>}
        <Button type="submit" fullWidth loading={reset.isPending}>
          Set password
        </Button>
      </form>
    </FormFrame>
  );
}
